/**
 * Lectura de códigos de barras con la cámara (tablet o celular).
 *
 * Android trae un lector en el navegador (`BarcodeDetector`); iPad y Windows
 * no, y ahí se usa zxing-cpp compilado a WebAssembly. El .wasm se sirve desde
 * nuestros propios assets y no desde el CDN que la librería usa por defecto.
 */

import zxingWasmUrl from 'zxing-wasm/reader/zxing_reader.wasm?url'

/** Los formatos de un comercio. Limitarlos acelera la lectura y evita falsos positivos. */
export const RETAIL_FORMATS = ['ean_13', 'ean_8', 'upc_a', 'upc_e', 'code_128', 'code_39'] as const

/** Un código que sigue frente a la cámara no vuelve a contar hasta que salga de cuadro este tiempo. */
export const CAMERA_REPEAT_MS = 1500

export interface CodeDetector {
  detect(source: HTMLVideoElement | HTMLCanvasElement): Promise<{ rawValue: string }[]>
}

/**
 * Recuadro de lectura, en fracción de lo que se ve en pantalla. Angosto y
 * horizontal porque los códigos de comercio son lineales: así no se lee el
 * producto de al lado y cada cuadro se procesa más rápido.
 */
export const SCAN_REGION = { x: 0.05, y: 0.32, width: 0.9, height: 0.36 } as const

/** Tolerancia alrededor del recuadro: un código que asoma un poco igual se lee. */
export const SCAN_REGION_MARGIN = 0.05

export interface Rect {
  x: number
  y: number
  width: number
  height: number
}

/**
 * Pasa el recuadro de pantalla a píxeles del video. El video se muestra con
 * `object-cover`, que lo agranda hasta llenar y recorta lo que sobra, así que
 * lo visible no es el cuadro entero.
 */
export function regionInVideo(
  videoSize: { width: number; height: number },
  displaySize: { width: number; height: number },
  region: Rect = SCAN_REGION,
  margin = SCAN_REGION_MARGIN
): Rect {
  const scale = Math.max(displaySize.width / videoSize.width, displaySize.height / videoSize.height)
  const visibleWidth = displaySize.width / scale
  const visibleHeight = displaySize.height / scale
  const offsetX = (videoSize.width - visibleWidth) / 2
  const offsetY = (videoSize.height - visibleHeight) / 2

  const x0 = Math.max(0, region.x - margin)
  const y0 = Math.max(0, region.y - margin)
  const x1 = Math.min(1, region.x + region.width + margin)
  const y1 = Math.min(1, region.y + region.height + margin)

  return {
    x: Math.round(offsetX + x0 * visibleWidth),
    y: Math.round(offsetY + y0 * visibleHeight),
    width: Math.round((x1 - x0) * visibleWidth),
    height: Math.round((y1 - y0) * visibleHeight),
  }
}

export function isCameraScanSupported() {
  return typeof window !== 'undefined' && window.isSecureContext && !!navigator.mediaDevices?.getUserMedia
}

/** En la PC se usa el lector de mano; la cámara se ofrece en tablets y celulares. */
export function shouldOfferCameraScan() {
  return isCameraScanSupported() && window.matchMedia('(any-pointer: coarse)').matches
}

/**
 * Descarta las lecturas repetidas del mismo código mientras siga a la vista:
 * cada vez que se ve renueva la espera, así que sostenerlo frente a la cámara
 * no lo suma de nuevo. Para sumar otra unidad hay que sacarlo y volver a
 * mostrarlo.
 */
export function createRepeatFilter(repeatMs = CAMERA_REPEAT_MS) {
  let lastCode: string | null = null
  let lastSeenAt = -Infinity

  return {
    accept(code: string, now: number): boolean {
      const repeated = code === lastCode && now - lastSeenAt < repeatMs
      lastCode = code
      lastSeenAt = now
      return !repeated
    },
  }
}

interface NativeBarcodeDetectorClass {
  new (options: { formats: string[] }): CodeDetector
  getSupportedFormats(): Promise<string[]>
}

async function loadNativeDetector(): Promise<CodeDetector | null> {
  const Native = (globalThis as { BarcodeDetector?: NativeBarcodeDetectorClass }).BarcodeDetector
  if (!Native) return null
  try {
    const supported = await Native.getSupportedFormats()
    const formats = RETAIL_FORMATS.filter((f) => supported.includes(f))
    // Sin EAN-13 no sirve para mercadería; mejor el de WebAssembly.
    if (!formats.includes('ean_13')) return null
    return new Native({ formats })
  } catch {
    return null
  }
}

let detectorPromise: Promise<CodeDetector> | null = null

export function loadCodeDetector(): Promise<CodeDetector> {
  detectorPromise ??= (async () => {
    const native = await loadNativeDetector()
    if (native) return native
    const { BarcodeDetector, prepareZXingModule } = await import('barcode-detector/ponyfill')
    // Se carga ya y no en el primer detect(): ahí un .wasm que no bajó se vería
    // como cuadros sin código, y la cámara quedaría "leyendo" sin leer nunca.
    await prepareZXingModule({
      overrides: {
        locateFile: (path: string, prefix: string) =>
          path.endsWith('.wasm') ? zxingWasmUrl : prefix + path,
      },
      fireImmediately: true,
    })
    return new BarcodeDetector({ formats: [...RETAIL_FORMATS] })
  })()
  // Si falló (sin red al bajar el .wasm), el próximo intento vuelve a probar.
  detectorPromise.catch(() => {
    detectorPromise = null
  })
  return detectorPromise
}
